import { useEffect, useRef, useState } from "react";
import { appBaseUrl, config } from "@/config";
import { usePublicStats } from "./usePublicStats";
import "./home.css";

/**
 * Landing page, v2 — started over 2026-07-06 on the founder's call.
 * Design rules: one column, one accent, no ornament, no animation theatre.
 * The only decoration is live production data. Every block has one job:
 * nav → claim → live feed → coverage → receipts → price → FAQ → footer.
 */

interface PubSignal {
  id: string;
  title: string;
  severity: "low" | "medium" | "high" | "critical";
  ruleName: string;
  tickers: string[];
  confidenceRate: number | null;
  estimatedEffect: { sigma: number; pct: number } | null;
  createdAt: number;
}

const SEV_COLOR: Record<PubSignal["severity"], string> = {
  critical: "#ff4438",
  high: "#ffb24d",
  medium: "#5fd3e0",
  low: "#6b7a72",
};

function utcHm(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}

const COVERAGE = [
  { name: "Power & grid", status: "measured", desc: "REMIT, UMM and ENTSO-E outage filings, parsed the second they post. Median alert ~19 minutes before the newswire." },
  { name: "Seismic", status: "measured", desc: "Own detector on ~7,800 stations plus USGS and EMSC. One quake becomes per-company signals with shaking intensity per site." },
  { name: "Industrial fires", status: "learning", desc: "Three VIIRS satellites over 25,000 mapped plants, with flare-aware thresholds so a working refinery isn't news." },
  { name: "Severe weather", status: "learning", desc: "Major events crossing mapped industrial assets. Only critical intersections surface." },
  { name: "Maritime", status: "monitored", desc: "Global AIS — dark vessels, jamming zones, sanctioned hulls. Tracked now, scored once it earns attribution." },
  { name: "Aircraft", status: "monitored", desc: "Global ADS-B including military airframes. Buildups alert only at critical mass." },
] as const;

// Exported for the build-time prerender (entry-server → scripts/prerender.mjs), which turns
// this same data into FAQPage JSON-LD — one source, no drift between the page and the schema.
export const FAQ = [
  { q: "Is this another chart-signal tool?", a: "No — there is no technical analysis anywhere in the system. EARLYWIRE reads primary sources (grid filings, transponders, seismic networks, satellites) and tells you which tickers each event touches. On power outages, the median alert has landed about 19 minutes before the newswire." },
  { q: "Why should I trust the scores?", a: "Every signal carries a confidence rate and an estimated effect taken from that category's own realized market moves — and the same numbers are published, continuously recomputed, on the scorecard. Categories that haven't earned an estimate say \"learning\" instead of pretending." },
  { q: "How is this different from a squawk?", a: "A squawk is fast audio without instruments attached. Here the event arrives sourced, scored, and already mapped to tickers — and you can check the service's lead time on the scorecard instead of taking it on faith." },
  { q: "If it's an edge, why sell it?", a: "We sell speed to ground truth, not a strategy. What you do with an event is your edge; a hundred desks can trade the same signal a hundred ways." },
  { q: "What happens after the free hour?", a: "The desk locks and nothing charges — there's no card on file. If the hour convinced you, you claim a seat and we reopen it the same day; if not, you walk away." },
] as const;

export function HomeScreen() {
  const s = usePublicStats();
  const [rows, setRows] = useState<PubSignal[] | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    const load = () => {
      fetch(`${config.restUrl}/api/public/signals?limit=6`)
        .then((r) => r.json())
        .then((j: { signals?: PubSignal[] }) => { if (alive.current && j.signals) setRows(j.signals); })
        .catch(() => { /* silence over fakery */ });
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { alive.current = false; clearInterval(id); };
  }, []);

  // Referral/invite codes are captured into THIS origin's localStorage (auth-context boot), but
  // the desk lives on app.earlywire.io — a different origin with different storage. Forward the
  // codes in the sign-in URL or the attribution silently dies at the origin boundary. Set after
  // hydration (useEffect) so the prerendered HTML (plain /signin) matches the first client render.
  const [signInUrl, setSignInUrl] = useState(appBaseUrl() + "/signin");
  useEffect(() => {
    try {
      const p = new URLSearchParams();
      const ref = localStorage.getItem("ew_ref");
      const invite = localStorage.getItem("ew_invite");
      if (ref) p.set("ref", ref);
      if (invite) p.set("invite", invite);
      const qs = p.toString();
      if (qs) setSignInUrl(`${appBaseUrl()}/signin?${qs}`);
    } catch { /* storage unavailable — plain link */ }
  }, []);

  return (
    <div className="lp">
      {/* nav */}
      <header className="lp-nav">
        <span className="lp-brand">EARLYWIRE</span>
        <nav>
          <a href="/proof">Scorecard</a>
          <a href={signInUrl}>Sign in</a>
          <a className="lp-cta" href={signInUrl}>Get started</a>
        </nav>
      </header>

      {/* claim */}
      <section className="lp-hero">
        <h1>Be first to know when a quake hits,<br />a refinery burns,<br />or the grid goes dark.</h1>
        <p>
          Every one of those events reprices something — the market just hasn't heard yet.
          EARLYWIRE reads the seismographs, satellites, ships and grid filings directly and
          hands you the affected tickers, a median of ~19 minutes before the newswire.
        </p>
        <div className="lp-actions">
          <a className="lp-btn" href={signInUrl}>Try the live desk — free</a>
          <a className="lp-link" href="/proof">See the live scorecard →</a>
        </div>
        <div className="lp-stats">
          {Math.round(s.objects / 1000)}k objects live · {Math.round(s.assets / 1000)}k mapped assets · {s.sources} feeds
          {s.latencyP50Ms != null && s.latencyP50Ms < 1000 ? ` · ${Math.round(s.latencyP50Ms)}ms p50` : ""}
        </div>
        <div className="lp-note">Normal signup, no card, no sales call — you're on the live desk in 60 seconds, free for an hour.</div>
      </section>

      {/* the product, live */}
      <section className="lp-sec">
        <h2>The live feed</h2>
        <p className="lp-sub">Real production signals, shown with a 1-minute public delay. Operators see them instantly.</p>
        <div className="lp-feed">
          {rows === null && <div className="lp-row lp-quiet">connecting…</div>}
          {rows?.length === 0 && <div className="lp-row lp-quiet">Quiet minute on the desk — the next signal appears here the moment it clears the delay.</div>}
          {rows?.map((r) => (
            <a className="lp-row" key={r.id} href={`/signal/${encodeURIComponent(r.id)}`} target="_blank" rel="noopener">
              <span className="lp-time">{utcHm(r.createdAt)}</span>
              <span className="lp-dot" style={{ background: SEV_COLOR[r.severity] }} title={r.severity} />
              <span className="lp-title">{r.title}</span>
              <span className="lp-meta">
                {r.tickers.slice(0, 3).join(" ")}
                {r.confidenceRate != null ? ` · ${Math.round(r.confidenceRate * 100)}%` : ""}
                {r.estimatedEffect ? ` · ~${r.estimatedEffect.sigma.toFixed(1)}σ` : ""}
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* coverage */}
      <section className="lp-sec">
        <h2>What it watches</h2>
        <p className="lp-sub">
          Each category is labeled by how far its proof has come. <b>Measured</b> means lead time and
          realized moves are public on the <a href="/proof">scorecard</a>; nothing is promoted before it earns it.
        </p>
        <div className="lp-cov">
          {COVERAGE.map((c) => (
            <div className="lp-cov-row" key={c.name}>
              <div className="lp-cov-head">
                <span className="lp-cov-name">{c.name}</span>
                <span className={`lp-tag ${c.status}`}>{c.status}</span>
              </div>
              <p>{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* receipts */}
      <section className="lp-sec">
        <h2>The receipts</h2>
        <p className="lp-sub">
          Every alert's lead time against the newswire and every tradeable market move (the furthest
          excursion within 4 traded hours) is recorded and recomputed continuously — the same tables
          the product runs on.
        </p>
        <div className="lp-proof">
          <div><b>99%</b><span>of matched power-outage alerts beat the wire</span></div>
          <div><b>~19 min</b><span>median lead on those alerts</span></div>
          <div><b>~0.5%</b><span>median own-equity tradeable move within 4h</span></div>
        </div>
        <a className="lp-link" href="/proof">Check the numbers yourself →</a>
      </section>

      {/* price */}
      <section className="lp-sec" id="access">
        <h2>One seat. $299/month.</h2>
        <p className="lp-sub">
          The full desk: live signals with confidence and estimated effect, the map, situation graphs,
          alerts to Slack and webhooks, desk chat, and the scorecard. Sign up, test everything free for
          an hour, then pick a seat — no card until you do.
        </p>
        <div className="lp-founding">
          <b>Early cohorts pay less, forever:</b> the ladder starts at <b>$149/month</b> and steps up
          as seats fill ($149 → $199 → $249 → $299 list). Whatever rate you join at is
          <b> locked for as long as you stay</b> — in return, use the suggestions board and shape
          what gets built.
        </div>
        <div className="lp-actions">
          <a className="lp-btn" href={signInUrl}>Claim a founding seat</a>
        </div>
        <div className="lp-note">Running a team? Mail <a href="mailto:info@earlywire.io">info@earlywire.io</a>.</div>
      </section>

      {/* objections */}
      <section className="lp-sec">
        <h2>Questions</h2>
        {FAQ.map((f) => (
          <details className="lp-faq" key={f.q}>
            <summary>{f.q}</summary>
            <p>{f.a}</p>
          </details>
        ))}
      </section>

      {/* footer */}
      <footer className="lp-foot">
        <div>
          <a href="/proof">Scorecard</a> · <a href="/terms">Terms</a> · <a href="/privacy">Privacy</a> · <a href="mailto:info@earlywire.io">Contact</a> · <a href={signInUrl}>Operator sign-in</a>
        </div>
        <p>© 2026 EARLYWIRE. Informational event signals from licensed and public data — not investment advice.</p>
      </footer>
    </div>
  );
}
